import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Users, Broadcast, Heart, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TurandotInscripcionesPage = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const formRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    cargo: '',
    municipio: '',
    email: '',
    telefono: '',
    aforo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.municipio || !formData.email || !formData.telefono) {
      toast.error('Por favor, complete todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/turandot-inscripciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('¡Inscripción enviada correctamente!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al enviar la inscripción');
      }
    } catch (err) {
      toast.error('Error de conexión. Inténtelo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Trophy,
      title: 'Prestigio Internacional',
      description: 'Sitúe a su municipio en la red selecta de ciudades que conmemoran el centenario operístico.',
      span: 'md:col-span-6'
    },
    {
      icon: Users,
      title: 'Democratización de la Cultura',
      description: 'Ofrezca a sus vecinos la experiencia de una entrada de 200€ por un coste de gestión mínimo. Es cultura de élite para todos.',
      span: 'md:col-span-6'
    },
    {
      icon: Broadcast,
      title: 'Tecnología BRAVO (Exclusiva)',
      description: 'Olvídese de los problemas de conexión. Gracias al sistema Bravo, garantizamos una señal de streaming profesional, estable y en alta definición, diseñada específicamente para auditorios y grandes recintos.',
      span: 'md:col-span-7'
    },
    {
      icon: Heart,
      title: 'Impacto Social "Vincerò"',
      description: 'Únase a la causa solidaria vinculada a la retransmisión para apoyar la investigación del cáncer infantil. Un evento con alma que moviliza a la ciudadanía.',
      span: 'md:col-span-5'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Grain texture overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
      }} />

      <Header onOpenAuth={() => setAuthOpen(true)} />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-end pb-20">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1761229660731-891484da5c35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxvcGVyYSUyMHR1cmFuZG90JTIwc3RhZ2UlMjBkcmFtYXRpYyUyMHJlZCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3NTU3MjU3OXww&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          {/* Logos */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-6 mb-8"
          >
            <img 
              src="/palau-les-arts-logo.svg" 
              alt="Palau de les Arts" 
              className="h-16 md:h-20 brightness-0 invert"
            />
            <div className="w-px h-12 bg-white/30" />
            <img 
              src="/bravo-logo.svg" 
              alt="Bravo" 
              className="h-8 md:h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </motion.div>

          {/* Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl md:text-5xl lg:text-6xl text-white font-bold tracking-tight leading-tight max-w-5xl mb-6"
          >
            EL PALAU DE LES ARTS Y VERSIÓN DIGITAL LLEVAN EL CENTENARIO DE{' '}
            <span className="text-[#e4002b]">TURANDOT</span>{' '}
            A LOS MUNICIPIOS DE LA COMUNITAT VALENCIANA
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-white/80 text-lg md:text-xl max-w-3xl mb-10"
          >
            Convierta su Auditorio en una extensión del Palau de les Arts en el Centenario de la obra maestra de Puccini.
          </motion.p>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            onClick={scrollToForm}
            data-testid="hero-cta-btn"
            className="px-8 py-4 bg-[#e4002b] text-white text-sm font-semibold tracking-widest uppercase hover:bg-[#c90025] transition-colors duration-300"
          >
            Quiero más información para mi municipio
          </motion.button>
        </div>
      </section>

      {/* Event Info Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Date */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center md:text-left"
            >
              <p className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase mb-4">Retransmisión en directo</p>
              <div className="flex items-baseline gap-4 justify-center md:justify-start">
                <span className="text-7xl md:text-8xl lg:text-9xl text-white font-bold">13</span>
                <div className="text-left">
                  <p className="text-2xl md:text-3xl text-white font-bold">Junio</p>
                  <p className="text-2xl md:text-3xl text-white/60">2026</p>
                </div>
              </div>
              <p className="text-white/60 text-lg mt-4">19:00h desde la Sala Principal</p>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <p className="text-white/70 leading-relaxed">
                El Palau de les Arts Reina Sofía, en colaboración con Versión Digital, anuncia el lanzamiento de una ambiciosa iniciativa cultural para conmemorar el centenario del estreno mundial de Turandot (1926-2026). La producción estrella de la temporada será retransmitida en directo desde la Sala Principal de Les Arts hacia una red de auditorios municipales y salas públicas de toda la geografía española.
              </p>
              <p className="text-white/70 leading-relaxed">
                El proyecto busca democratizar el acceso a la alta cultura, permitiendo que miles de ciudadanos disfruten del célebre <em className="text-white/90">"Nessun dorma"</em> con la misma intensidad y calidad que los asistentes al teatro.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 px-6 md:px-12 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-white/80 leading-relaxed">
              Para garantizar una experiencia cinematográfica sin precedentes, la retransmisión se apoyará en la <strong className="text-[#e4002b]">tecnología Bravo</strong>, un sistema de streaming profesional diseñado para superar las limitaciones de las conexiones domésticas. Esta solución técnica garantiza una señal estable en alta definición y sonido envolvente, permitiendo que cualquier auditorio municipal se convierta, por una noche, en una extensión del Palau de les Arts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-white font-bold tracking-tight">
              ¿POR QUÉ SU AYUNTAMIENTO DEBE LIDERAR ESTA INICIATIVA?
            </h2>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-12 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -4, boxShadow: '0 8px 30px rgba(228,0,43,0.15)' }}
                className={`${benefit.span} p-8 bg-[#141414] border border-white/10 rounded-xl transition-all duration-300 group`}
              >
                <benefit.icon 
                  weight="duotone" 
                  className="w-12 h-12 text-[#e4002b] mb-6 group-hover:scale-110 transition-transform duration-300" 
                />
                <h3 className="text-xl md:text-2xl text-white font-bold mb-4">{benefit.title}</h3>
                <p className="text-white/60 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section 
        className="py-24 px-6 md:px-12 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1759502418494-bee52af25972?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHx0aGVhdGVyJTIwYXVkaXRvcml1bSUyMGF1ZGllbmNlJTIwZ3JhbmR8ZW58MHx8fHwxNzc1NTcyNTg1fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#0a0a0a]/90" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl lg:text-4xl text-white font-medium italic leading-relaxed mb-8"
          >
            "Nuestro objetivo es que la distancia geográfica no sea una barrera para la emoción. Con Turandot no solo celebramos un centenario, sino la capacidad de la tecnología para vertebrar el territorio a través del arte."
          </motion.blockquote>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-[#e4002b] font-semibold tracking-widest uppercase text-sm">Carlos Carbó</p>
            <p className="text-white/60">CEO de Versión Digital</p>
          </motion.div>
        </div>
      </section>

      {/* CTA & Form Section */}
      <section 
        ref={formRef}
        className="py-24 px-6 md:px-12 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1759851358346-23ae8c50d31c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx0aGVhdGVyJTIwYXVkaXRvcml1bSUyMGF1ZGllbmNlJTIwZ3JhbmR8ZW58MHx8fHwxNzc1NTcyNTg1fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#0a0a0a]/95" />
        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-white font-bold tracking-tight mb-6">
              ¡NO PIERDA ESTA OPORTUNIDAD ÚNICA!
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              El calendario de adhesiones para municipios está abierto y las plazas de conexión son limitadas para garantizar la asistencia técnica personalizada.
            </p>
            <p className="text-[#e4002b] text-xl mt-4 font-semibold">
              Haga que su auditorio brille con luz propia.
            </p>
            <p className="text-white/50 text-sm mt-4 italic">
              Si su municipio no pertenece a la Comunitat Valenciana, también puede unirse.
            </p>
          </motion.div>

          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-[#0a0a0a]/80 backdrop-blur-2xl border border-white/10 rounded-xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          >
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle weight="duotone" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl text-white font-bold mb-2">¡Gracias por su interés!</h3>
                <p className="text-white/70">
                  Hemos recibido sus datos y nuestro equipo de gestión cultural se pondrá en contacto con usted en menos de 24 horas.
                </p>
              </div>
            ) : (
              <>
                <p className="text-white/60 text-center mb-8">
                  Déjenos sus datos y nuestro equipo técnico y de gestión cultural se pondrá en contacto con usted en menos de 24 horas.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Nombre y apellidos *</label>
                      <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Su nombre completo"
                        data-testid="input-nombre"
                        className="w-full px-4 py-3 bg-transparent border-b border-white/20 focus:border-[#e4002b] text-white placeholder:text-white/40 outline-none transition-colors duration-300"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Cargo *</label>
                      <input
                        type="text"
                        value={formData.cargo}
                        onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                        placeholder="Ej: Concejal de Cultura"
                        data-testid="input-cargo"
                        className="w-full px-4 py-3 bg-transparent border-b border-white/20 focus:border-[#e4002b] text-white placeholder:text-white/40 outline-none transition-colors duration-300"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Municipio *</label>
                      <input
                        type="text"
                        value={formData.municipio}
                        onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                        placeholder="Nombre del municipio"
                        data-testid="input-municipio"
                        className="w-full px-4 py-3 bg-transparent border-b border-white/20 focus:border-[#e4002b] text-white placeholder:text-white/40 outline-none transition-colors duration-300"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Email de contacto *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@ayuntamiento.es"
                        data-testid="input-email"
                        className="w-full px-4 py-3 bg-transparent border-b border-white/20 focus:border-[#e4002b] text-white placeholder:text-white/40 outline-none transition-colors duration-300"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Teléfono de contacto *</label>
                      <input
                        type="tel"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="+34 600 000 000"
                        data-testid="input-telefono"
                        className="w-full px-4 py-3 bg-transparent border-b border-white/20 focus:border-[#e4002b] text-white placeholder:text-white/40 outline-none transition-colors duration-300"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Aforo aprox. del recinto</label>
                      <input
                        type="text"
                        value={formData.aforo}
                        onChange={(e) => setFormData({ ...formData, aforo: e.target.value })}
                        placeholder="Ej: 300 personas"
                        data-testid="input-aforo"
                        className="w-full px-4 py-3 bg-transparent border-b border-white/20 focus:border-[#e4002b] text-white placeholder:text-white/40 outline-none transition-colors duration-300"
                      />
                    </div>
                  </div>

                  <div className="pt-6 text-center">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="submit-btn"
                      className="px-12 py-4 bg-[#e4002b] text-white text-sm font-semibold tracking-widest uppercase hover:bg-[#c90025] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Enviando...' : 'Solicitar información'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
};

export default TurandotInscripcionesPage;
